import { useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Loader2, Activity } from "lucide-react";
import { toast } from "sonner";
import { StoreButtons } from "@/components/landing/StoreButtons";
import { useLang } from "@/i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Download = () => {
  const { t } = useLang();
  return (
    <section
      id="telecharger"
      data-testid="download-section"
      className="relative py-24 sm:py-32"
    >
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <div className="relative overflow-hidden rounded-[2.5rem] border border-white/10 bg-gradient-to-b from-white/[0.06] to-transparent px-8 py-16 sm:px-16 sm:py-20 text-center">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-emerald-500/25 blur-3xl" />
          <div className="absolute -bottom-24 right-10 h-64 w-64 rounded-full bg-violet-500/20 blur-3xl" />
          <div className="absolute inset-0 grid-dots opacity-40" />
          <div className="relative">
            <h2 className="font-outfit text-4xl sm:text-5xl font-extrabold tracking-tight text-white">
              {t.download.title}
            </h2>
            <p className="mt-5 mx-auto max-w-xl text-lg text-slate-400">
              {t.download.subtitle}
            </p>
            <StoreButtons className="mt-10 justify-center" />
          </div>
        </div>
      </div>
    </section>
  );
};

export const Footer = () => {
  const { t } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/subscribe`, { email });
      toast.success(t.footer.subOk);
      setEmail("");
    } catch (err) {
      const msg = err?.response?.data?.detail || t.footer.subErr;
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      id="contact"
      data-testid="footer-section"
      className="border-t border-white/10 bg-[#070A12]"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-16 grid lg:grid-cols-2 gap-12">
        <div>
          <div className="flex items-center gap-2 font-outfit font-extrabold text-2xl text-white">
            <span className="grid place-items-center h-9 w-9 rounded-xl bg-emerald-400 text-emerald-950">
              <Activity className="h-5 w-5" strokeWidth={2.75} />
            </span>
            <span>
              Jos'<span className="text-emerald-400">FIT</span>
            </span>
          </div>
          <p className="mt-4 max-w-sm text-slate-400">{t.footer.tagline}</p>
          <div className="mt-6 flex flex-wrap gap-4 text-sm text-slate-500">
            <a href="mailto:contact@josfit.app" className="hover:text-emerald-400" data-testid="footer-link-contact">
              contact@josfit.app
            </a>
            <a href="/#byok" className="hover:text-emerald-400" data-testid="footer-link-byok">
              {t.nav.byok}
            </a>
            <Link to="/confidentialite" className="hover:text-emerald-400" data-testid="footer-link-privacy">
              {t.footer.privacy}
            </Link>
            <Link to="/cgu" className="hover:text-emerald-400" data-testid="footer-link-cgu">
              {t.footer.cgu}
            </Link>
          </div>
        </div>

        <div className="lg:justify-self-end w-full max-w-md">
          <h3 className="font-outfit text-xl font-bold text-white">{t.footer.newsTitle}</h3>
          <p className="mt-2 text-slate-400">{t.footer.newsDesc}</p>
          <form
            onSubmit={subscribe}
            data-testid="newsletter-form"
            className="mt-5 flex flex-col sm:flex-row gap-3"
          >
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t.footer.placeholder}
              data-testid="newsletter-email-input"
              className="flex-1 rounded-full border border-white/10 bg-white/[0.04] px-5 py-3 text-white placeholder:text-slate-500 outline-none focus:border-emerald-400/60 focus:ring-1 focus:ring-emerald-400/40"
            />
            <button
              type="submit"
              disabled={loading}
              data-testid="newsletter-submit"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-emerald-400 px-7 py-3 text-sm font-bold text-emerald-950 hover:bg-emerald-300 transition-colors duration-300 disabled:opacity-60 shadow-lg shadow-emerald-500/25"
            >
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              {t.footer.subscribe}
            </button>
          </form>
        </div>
      </div>

      <div className="border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 text-center text-sm text-slate-500">
          © {new Date().getFullYear()} Jos'FIT · {t.footer.rights}
        </div>
      </div>
    </footer>
  );
};
